import { type AsyncResult, type VoidAsyncResult, asVoidAsyncResult } from "@/cache/AsyncResult";
import { networkCaches } from "@/cache/GlobalCache";
import { thunderstormTrackingLayerConfig } from "@/constants/layerConfigAttributes";
import { ChangeDetector } from "@/layers/ChangeDetector";
import type { ScenePublicApi } from "@/layers/Compositor";
import type { CheckedProps, LayerBase, PropsChecker } from "@/layers/LayerBase";
import { MapboxGeoJSONLayer } from "@/layers/geojson/MapboxGeoJSONLayer";
import {
  THUNDERSTORM_SEVERITY_ORDER,
  THUNDERSTORM_TYPE_ORDER,
  type ThunderstormSeverity,
  type ThunderstormType,
} from "@/layers/geojson/thunderstormConstants";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { renderPopup } from "@/layers/utility/popup";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import { t } from "@lingui/core/macro";
import type { GeoJSONFeatureCollection } from "@mm/api.meteomatics.com";
import type { GenericGeoJsonRequest } from "@mm/api.meteomatics.com";
import type { GuiTimeZone, ThunderstormLayer } from "@mm/metx-workbench.meteomatics.com";
import { debounce } from "lodash";
import type { DateTime } from "luxon";
import {
  type AnyLayer,
  type AnyLayout,
  type Expression,
  type MapMouseEvent,
  type MapboxGeoJSONFeature,
  type Map as MapboxMap,
  type PaintSpecification,
  Popup,
} from "mapbox-gl";

const SEVERITY_COLORS: Record<ThunderstormSeverity, string> = {
  unclassified: "grey",
  low: "yellow",
  mid: "orange",
  high: "red",
  decaying: "#8a8a8a",
};

const severityLabels: Record<ThunderstormSeverity, () => string> = {
  decaying: () => t`Decaying`,
  unclassified: () => t`Unclassified`,
  low: () => t`Low`,
  mid: () => t`Moderate`,
  high: () => t`High`,
};

const typeLabels: Record<ThunderstormType, () => string> = {
  history: () => t`Historical Track`,
  current: () => t`Active Cell`,
  nowcast: () => t`Impact Area`,
};

type SeverityLayerMap = Record<ThunderstormSeverity, string>;

export class ThunderstormTrackingLayerImpl extends MapboxGeoJSONLayer<ThunderstormLayer> {
  private readonly currentPolygonFillLayerIds: SeverityLayerMap;
  private readonly currentPolygonOutlineLayerIds: SeverityLayerMap;
  private readonly nowcastPolygonFillLayerIds: SeverityLayerMap;
  private readonly nowcastPolygonOutlineLayerIds: SeverityLayerMap;
  private readonly historyLineLayerIds: SeverityLayerMap;
  private readonly historyPointLayerIds: SeverityLayerMap;
  private readonly currentPointLayerIds: SeverityLayerMap;
  private readonly allLayerIds: string[] = [];
  private readonly displayTimeChanged = new ChangeDetector<number>();
  private wasAnimationMode = false;
  private hoverLayerIds: string[] = [];
  private readonly onMouseEnter = () => {
    const map = this.scene.getMapboxMap();
    map.getCanvas().style.cursor = "pointer";
  };
  private readonly onMouseLeave = () => {
    const map = this.scene.getMapboxMap();
    map.getCanvas().style.cursor = "";
    this.popup.remove();
  };
  private readonly onMouseMove = (e: MapMouseEvent) => {
    const features = (e.features ?? []) as MapboxGeoJSONFeature[];
    if (!features.length) {
      return;
    }
    const feature = features[0];
    const props = feature.properties ?? {};
    const severity =
      props.severity == null
        ? null
        : severityLabels[props.severity as ThunderstormSeverity]?.() ?? String(props.severity);
    const type = props.type == null ? null : typeLabels[props.type as ThunderstormType]?.() ?? String(props.type);
    const rows: Array<[string, unknown]> = [
      [t`Storm Stage`, type],
      [t`Severity`, severity],
      [t`Timestamp`, props.timestamp],
    ];
    const html = renderPopup({
      title: t`Thunderstorm`,
      severity: props.severity ?? null,
      rows,
      theme: "thunderstorm",
    });

    this.popup.setLngLat(e.lngLat).setHTML(html).addTo(this.scene.getMapboxMap());
  };
  private popup = new Popup({
    maxWidth: "90vw",
    closeButton: false,
    className: "mapbox-popover mapbox-popover-nwa",
  });
  private debouncedUpdate = debounce(
    () => {
      this.updateTrigger();
    },
    800,
    { leading: false, maxWait: 1500 },
  );

  constructor(id: number, props: ThunderstormLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone, "symbol");

    this.currentPolygonFillLayerIds = this.buildSeverityLayerIds("current-fill");
    this.currentPolygonOutlineLayerIds = this.buildSeverityLayerIds("current-outline");
    this.nowcastPolygonFillLayerIds = this.buildSeverityLayerIds("nowcast-fill");
    this.nowcastPolygonOutlineLayerIds = this.buildSeverityLayerIds("nowcast-outline");
    this.historyLineLayerIds = this.buildSeverityLayerIds("history-line");
    this.historyPointLayerIds = this.buildSeverityLayerIds("history-point");
    this.currentPointLayerIds = this.buildSeverityLayerIds("current-point");

    // Base layer is unused; keep it hidden.
    this.updateMapboxLayoutProperties({ visibility: "none" });

    this.addCurrentPolygons();
    this.addCurrentOutlines();
    this.addNowcastPolygons();
    this.addNowcastOutlines();
    this.addHistoryLines();
    this.addHistoryPoints();
    this.addCurrentPoints();

    this.applyOpacity(this.props.opacity ?? 1);
    this.applyVisibility();
    this.setEventListeners();
  }

  humanReadableId(): string {
    return safeFmt`metx.thunderstormtracking#${this.uid}`;
  }

  beforeRender(): void {
    if (
      this.displayTimeChanged.changed(
        roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName).toMillis(),
      )
    ) {
      this.popup.remove();
    }

    const isAnimationMode = this.scene.isAnimation();
    if (!isAnimationMode && this.wasAnimationMode) {
      networkCaches.geojson_cache.cancelPendingGeoJsonRequests({
        kind: this.props.kind,
        path: "get_thunderstorm_tracks",
      });
    }
    this.wasAnimationMode = isAnimationMode;

    if (isAnimationMode) {
      this.updateTrigger();
    } else {
      this.debouncedUpdate();
    }
  }

  updateTrigger(): void {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);

    const result = networkCaches.geojson_cache.retrieveGeoJsonEndpoint(request, {
      kind: this.props.kind,
      owner: this.getGeoJsonRequestOwner(),
    });
    this.cancelSupersededGeoJsonRequests(result);
    // Keep last data while pending when time is unchanged; clear immediately on time change.
    const timeChanged = this.getTimeChanged().changed(dateTimeWithOffset.toMillis());
    this.updateSource(result, dateTimeWithOffset, undefined, {
      // Keep last data while loading even when time changes (spinner indicates loading).
      keepLastDataWhilePending: true,
      timeChangedOverride: timeChanged,
    });
  }

  checker(): PropsChecker<ThunderstormLayer, LayerBase<ThunderstormLayer>> {
    type CheckerProps = CheckedProps<ThunderstormLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (!changed) {
        return false;
      }

      this.setLayerProps({ [property]: curr[property] });

      switch (property) {
        case "show":
          this.applyVisibility();
          break;
        case "opacity":
          this.applyOpacity(curr.opacity ?? 1);
          break;
        case "custom_options":
          this.applyVisibility();
          break;
        case "calibrated":
          break;
        default: {
          const _exhaustive: never = property;
          return _exhaustive;
        }
      }
      return true;
    };

    return {
      show: checkLayerProp("show"),
      opacity: checkLayerProp("opacity"),
      custom_options: checkLayerProp("custom_options"),
      calibrated: checkLayerProp("calibrated"),
    };
  }

  createRequest(dateTimeWithOffset: DateTime): GenericGeoJsonRequest {
    const { area } = this.computeBoundingBox(0);
    return {
      path: "get_thunderstorm_tracks",
      area,
      datetime: dateTimeWithOffset,
    };
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);

    const result = networkCaches.geojson_cache.retrieveGeoJsonEndpoint(request, {
      kind: this.props.kind,
    });
    return asVoidAsyncResult(result);
  }

  getActiveWeatherParametersAsString(): { model: string; parameter: string }[] {
    return [];
  }

  peekLayerCache(request: GenericGeoJsonRequest): AsyncResult<GeoJSONFeatureCollection> {
    return networkCaches.geojson_cache.peekGeoJsonEndpoint(request, {
      kind: this.props.kind,
    });
  }

  moveZIndex(beforeLayerId?: string) {
    const map = this.scene.getMapboxMap();
    super.moveZIndex(beforeLayerId);
    for (const id of this.orderedLayerIds()) {
      if (map.getLayer(id)) {
        map.moveLayer(id, beforeLayerId);
      }
    }
  }

  removeLayer(): void {
    this.debouncedUpdate.cancel();
    networkCaches.geojson_cache.cancelPendingGeoJsonRequests({
      kind: this.props.kind,
      path: "get_thunderstorm_tracks",
    });
    const map = this.scene.getMapboxMap();
    if (this.hoverLayerIds.length) {
      map.off("mouseenter", this.hoverLayerIds, this.onMouseEnter);
      map.off("mouseleave", this.hoverLayerIds, this.onMouseLeave);
      map.off("mousemove", this.hoverLayerIds, this.onMouseMove);
    }
    for (const id of this.allLayerIds) {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    }
    super.removeLayer();
  }

  private orderedLayerIds(): string[] {
    return [
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.currentPolygonFillLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.currentPolygonOutlineLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.nowcastPolygonFillLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.nowcastPolygonOutlineLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.historyLineLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.historyPointLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.currentPointLayerIds[severity]),
    ];
  }

  private buildSeverityLayerIds(suffix: string): SeverityLayerMap {
    return THUNDERSTORM_SEVERITY_ORDER.reduce((acc, severity) => {
      acc[severity] = `${this.humanReadableId()}-${suffix}-${severity}`;
      return acc;
    }, {} as SeverityLayerMap);
  }

  private addCurrentPolygons() {
    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.addLayer(this.currentPolygonFillLayerIds[severity], {
        type: "fill",
        paint: {
          "fill-color": SEVERITY_COLORS[severity],
          "fill-opacity": 0.6 * (this.props.opacity ?? 1),
        },
        filter: this.buildFilter({
          geometryTypes: ["Polygon", "MultiPolygon"],
          type: "current",
          severity,
        }),
      });
    }
  }

  private addCurrentOutlines() {
    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.addLayer(this.currentPolygonOutlineLayerIds[severity], {
        type: "line",
        paint: {
          "line-color": "#000000",
          "line-width": 2,
          "line-opacity": this.props.opacity ?? 1,
        },
        filter: this.buildFilter({
          geometryTypes: ["Polygon", "MultiPolygon"],
          type: "current",
          severity,
        }),
      });
    }
  }

  private addNowcastPolygons() {
    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.addLayer(this.nowcastPolygonFillLayerIds[severity], {
        type: "fill",
        paint: {
          "fill-color": SEVERITY_COLORS[severity],
          "fill-opacity": 0.4 * (this.props.opacity ?? 1),
        },
        filter: this.buildFilter({
          geometryTypes: ["Polygon", "MultiPolygon"],
          type: "nowcast",
          severity,
        }),
      });
    }
  }

  private addNowcastOutlines() {
    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.addLayer(this.nowcastPolygonOutlineLayerIds[severity], {
        type: "line",
        paint: {
          "line-color": "#808080",
          "line-width": 2,
          "line-opacity": this.props.opacity ?? 1,
        },
        filter: this.buildFilter({
          geometryTypes: ["Polygon", "MultiPolygon"],
          type: "nowcast",
          severity,
        }),
      });
    }
  }

  private addHistoryLines() {
    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.addLayer(this.historyLineLayerIds[severity], {
        type: "line",
        paint: {
          "line-color": "#000000",
          "line-width": 2,
          "line-opacity": this.props.opacity ?? 1,
        },
        filter: this.buildFilter({
          geometryTypes: ["LineString", "MultiLineString"],
          type: "history",
          severity,
        }),
      });
    }
  }

  private addHistoryPoints() {
    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.addLayer(this.historyPointLayerIds[severity], {
        type: "circle",
        paint: {
          "circle-radius": thunderstormTrackingLayerConfig.historyPointRadius,
          "circle-color": SEVERITY_COLORS[severity],
          "circle-opacity": this.props.opacity ?? 1,
          "circle-stroke-color": "#000000",
          "circle-stroke-width": 1,
          "circle-stroke-opacity": this.props.opacity ?? 1,
        },
        filter: this.buildFilter({
          geometryTypes: ["Point", "MultiPoint"],
          type: "history",
          severity,
        }),
      });
    }
  }

  private addCurrentPoints() {
    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.addLayer(this.currentPointLayerIds[severity], {
        type: "circle",
        paint: {
          "circle-radius": thunderstormTrackingLayerConfig.currentPointRadius,
          "circle-color": SEVERITY_COLORS[severity],
          "circle-opacity": this.props.opacity ?? 1,
          "circle-stroke-color": "#000000",
          "circle-stroke-width": 2,
          "circle-stroke-opacity": this.props.opacity ?? 1,
        },
        filter: this.buildFilter({
          geometryTypes: ["Point", "MultiPoint"],
          type: "current",
          severity,
        }),
      });
    }
  }

  private addLayer(id: string, layer: Omit<AnyLayer, "id" | "source" | "layout"> & { layout?: AnyLayout }) {
    this.scene.getMapboxMap().addLayer({
      id,
      source: this.mapbox_source,
      layout: {
        visibility: this.props.show ? "visible" : "none",
        ...(layer.layout ?? {}),
      },
      ...layer,
    });
    this.allLayerIds.push(id);
  }

  private buildFilter(options: {
    geometryTypes: string[];
    type: ThunderstormType;
    severity: ThunderstormSeverity;
  }): Expression {
    const isHistoryLine =
      options.type === "history" &&
      (options.geometryTypes.includes("LineString") || options.geometryTypes.includes("MultiLineString"));
    const severityFilter =
      isHistoryLine && options.severity === "unclassified"
        ? ["any", ["==", ["get", "severity"], "unclassified"], ["!", ["has", "severity"]]]
        : ["==", ["get", "severity"], options.severity];
    return [
      "all",
      ["match", ["geometry-type"], options.geometryTypes, true, false],
      ["==", ["get", "type"], options.type],
      severityFilter,
    ];
  }

  private applyVisibility() {
    const map = this.scene.getMapboxMap();
    const severityFilter = this.normalizeFilter(this.props.custom_options?.severity_filter ?? null);
    const typeFilter = this.normalizeFilter(this.props.custom_options?.types_filter ?? null);
    this.updateMapboxLayoutProperties({ visibility: "none" });

    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.setLayerVisibility(
        map,
        this.currentPolygonFillLayerIds[severity],
        this.shouldShow("current", severity, typeFilter, severityFilter),
      );
      this.setLayerVisibility(
        map,
        this.currentPolygonOutlineLayerIds[severity],
        this.shouldShow("current", severity, typeFilter, severityFilter),
      );
      this.setLayerVisibility(
        map,
        this.nowcastPolygonFillLayerIds[severity],
        this.shouldShow("nowcast", severity, typeFilter, severityFilter),
      );
      this.setLayerVisibility(
        map,
        this.nowcastPolygonOutlineLayerIds[severity],
        this.shouldShow("nowcast", severity, typeFilter, severityFilter),
      );
      this.setLayerVisibility(
        map,
        this.historyLineLayerIds[severity],
        this.shouldShow("history", severity, typeFilter, severityFilter),
      );
      this.setLayerVisibility(
        map,
        this.historyPointLayerIds[severity],
        this.shouldShow("history", severity, typeFilter, severityFilter),
      );
      this.setLayerVisibility(
        map,
        this.currentPointLayerIds[severity],
        this.shouldShow("current", severity, typeFilter, severityFilter),
      );
    }
  }

  private shouldShow(
    type: ThunderstormType,
    severity: ThunderstormSeverity,
    typeFilter: Set<string> | null,
    severityFilter: Set<string> | null,
  ): boolean {
    if (!this.props.show) {
      return false;
    }
    if (typeFilter && !typeFilter.has(type)) {
      return false;
    }
    if (severityFilter && !severityFilter.has(severity)) {
      return false;
    }
    return true;
  }

  private normalizeFilter(filter: string[] | null): Set<string> | null {
    if (!filter || filter.length === 0) {
      return null;
    }
    return new Set(filter);
  }

  private setLayerVisibility(map: MapboxMap, id: string, visible: boolean) {
    if (!map.getLayer(id)) {
      return;
    }
    map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
  }

  private applyOpacity(opacity: number) {
    const map = this.scene.getMapboxMap();
    for (const severity of THUNDERSTORM_SEVERITY_ORDER) {
      this.updatePaint(map, this.currentPolygonFillLayerIds[severity], { "fill-opacity": 0.6 * opacity });
      this.updatePaint(map, this.currentPolygonOutlineLayerIds[severity], { "line-opacity": opacity });
      this.updatePaint(map, this.nowcastPolygonFillLayerIds[severity], { "fill-opacity": 0.4 * opacity });
      this.updatePaint(map, this.nowcastPolygonOutlineLayerIds[severity], { "line-opacity": opacity });
      this.updatePaint(map, this.historyLineLayerIds[severity], { "line-opacity": opacity });
      this.updatePaint(map, this.historyPointLayerIds[severity], {
        "circle-opacity": opacity,
        "circle-stroke-opacity": opacity,
      });
      this.updatePaint(map, this.currentPointLayerIds[severity], {
        "circle-opacity": opacity,
        "circle-stroke-opacity": opacity,
      });
    }
  }

  private updatePaint(map: MapboxMap, id: string, props: Partial<PaintSpecification>) {
    if (!map.getLayer(id)) {
      return;
    }
    for (const key in props) {
      const name = key as keyof PaintSpecification;
      const value = props[name];
      if (value !== undefined) {
        map.setPaintProperty(id, name, value);
      }
    }
  }

  private setEventListeners() {
    const map = this.scene.getMapboxMap();
    this.hoverLayerIds = [
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.currentPolygonFillLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.nowcastPolygonFillLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.historyPointLayerIds[severity]),
      ...THUNDERSTORM_SEVERITY_ORDER.map((severity) => this.currentPointLayerIds[severity]),
    ];

    map.on("mouseenter", this.hoverLayerIds, this.onMouseEnter);
    map.on("mouseleave", this.hoverLayerIds, this.onMouseLeave);
    map.on("mousemove", this.hoverLayerIds, this.onMouseMove);
  }
}
